import { Link } from "react-router-dom";
import AuthImage from "./AuthImage";
import { User, Shield } from "lucide-react";

/**
 * Globally reusable, clickable avatar. Links to /u/{handle} when handle is
 * available, otherwise falls back to /profile/{id} or /org/{id}. Adds a
 * subtle hover ring + opacity to give clear visual feedback that the avatar
 * is interactive.
 */
const SIZES = {
  xs: { box: "w-6 h-6", icon: 10 },
  sm: { box: "w-8 h-8", icon: 12 },
  md: { box: "w-9 h-9", icon: 14 },
  lg: { box: "w-10 h-10", icon: 14 },
  xl: { box: "w-12 h-12", icon: 16 },
};

export default function UserAvatar({
  user,
  size = "sm",
  className = "",
  showRing = true,
  // optional override: link to a specific path
  to,
  "data-testid": testId,
}) {
  if (!user) return null;
  const { box, icon } = SIZES[size] || SIZES.sm;
  const isOrg = user.type === "org";
  const handle = user.handle;
  const href =
    to ||
    (handle
      ? `/u/${handle}`
      : isOrg
      ? `/org/${user.id}`
      : `/profile/${user.id}`);
  const ringCls = showRing
    ? "hover:opacity-85 hover:ring-2 hover:ring-[#00aaff] focus:ring-2 focus:ring-[#00aaff]"
    : "";

  return (
    <Link
      to={href}
      className={`relative inline-block rounded-full ${ringCls} transition-all duration-150 ${className}`}
      data-testid={testId || `avatar-link-${handle || user.id}`}
      title={user.name || handle || ""}
      aria-label={`Open profile of ${user.name || handle || "user"}`}
    >
      <div className={`${box} rounded-full overflow-hidden border border-[rgba(0,170,255,0.18)] bg-[#0d1b2e]`}>
        <AuthImage
          src={user.avatar_url}
          alt={user.name || handle || ""}
          className="w-full h-full object-cover"
          fallback={
            <div className="w-full h-full flex items-center justify-center">
              {isOrg ? (
                <Shield size={icon} className="text-[#00aaff]" />
              ) : (
                <User size={icon} className="text-[#39ff14]" />
              )}
            </div>
          }
        />
      </div>
    </Link>
  );
}
